import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useEntity } from "../lib/entity";
import { Reveal, useSeo } from "../lib/ui";
import { StatusKey, StatusTag } from "../components/StatusTag";
import { RecommendedEntities } from "../components/RecommendedEntities";

const SEPARATION = [
  ["KickStartKenyaFootball", "An independent football research, heritage and development platform, run from Nairobi. It maps opportunity, costs projects and publishes verified information across the key 18 counties and all 47.", "verified"],
  ["FC Nairobi", "A separate professional club initiative. It is intended to be the first major implementation project emerging from the wider vision on this platform.", "development"],
  ["Kenya Premier League Ltd", "A separate organisation. The founder of KickStartKenyaFootball holds a professional role as its Commercial Director; that is a personal appointment and implies no institutional commitment by the league to this platform or to FC Nairobi.", "verified"],
];

const DEMONSTRATES = [
  ["Youth development", "An age-group pathway with recorded minutes, not trials.", "proposed"],
  ["Professional club management", "Incorporation, licensing, published accounts and solvency.", "development"],
  ["Football infrastructure", "Courts, pitches and a training base with costed running budgets.", "proposed"],
  ["Coaching and education", "Badged, paid coaches and schooling alongside football.", "proposed"],
  ["Women's football", "Equal facilities and coaching hours from the first season.", "proposed"],
  ["Community participation", "County leagues and school programmes around the club.", "proposed"],
  ["Commercial investment", "Priced, returnable capital rather than donations.", "proposed"],
  ["Sponsorship", "Measured audience sold on evidence.", "proposed"],
  ["Transparent player pathways", "Published routes in and out, and the contracts behind them.", "proposed"],
  ["Employment creation", "Jobs across the fifteen parts of the ecosystem.", "proposed"],
];

export default function FcNairobi() {
  const e = useEntity();

  useSeo({
    title: "KickStartKenyaFootball and FC Nairobi: From National Plan to First Implementation Project",
    description:
      "KickStartKenyaFootball is the wider national football research, heritage and development platform. FC Nairobi is the first major implementation project emerging from that wider vision. Separate organisations, clearly labelled.",
  });

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="fc-nairobi-page">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <p className="overline text-ochre">Separate organisations, one direction of travel</p>
        <h1 className="display-1 mt-5 max-w-4xl">
          KickStartKenyaFootball And FC Nairobi: From National Plan To First Implementation Project
        </h1>
        <p className="body-copy mt-6 max-w-3xl" data-testid="fcn-distinction">
          KickStartKenyaFootball is the wider national football research, heritage and development platform. FC
          Nairobi is the first major implementation project emerging from that wider vision.
        </p>

        <div className="mt-10 border-l-2 border-maasai bg-[#0b0b0b] p-6 md:p-8" data-testid="fcn-separation-note">
          <p className="overline text-ochre">Read this first</p>
          <p className="mt-4 text-sm leading-relaxed text-zinc-300 md:text-base">
            These are separate organisations and initiatives. Nothing on this platform should be read as Kenya
            Premier League Ltd owning, operating, endorsing or partnering with FC Nairobi or
            KickStartKenyaFootball, and nothing should be read as FC Nairobi being the delivery vehicle for every
            proposal published here. Where a written institutional agreement exists, it will be named and dated on
            this page.
          </p>
        </div>

        <div className="mt-12 grid gap-px bg-zinc-800" data-testid="fcn-separation">
          {SEPARATION.map(([name, body, status], i) => (
            <Reveal key={name} delay={Math.min(i * 0.05, 0.2)}>
              <div className="bg-black p-6 md:p-8">
                <div className="flex flex-wrap items-center gap-4">
                  <h2 className="font-display text-lg uppercase tracking-tight text-white md:text-xl">{name}</h2>
                  <StatusTag status={status} testId={`fcn-status-${i}`} />
                </div>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-300 md:text-base">{body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <h2 className="display-3 mt-20">What The First Project Is Meant To Demonstrate</h2>
        <p className="body-copy mt-5 max-w-3xl">
          FC Nairobi is intended to show that the principles identified through this platform work in practice. It
          is the first project, not the limit of the plan: KickStartKenyaFootball will continue to identify, cost
          and support projects in other counties, independently or alongside county and national government, the
          federation, the league and other professional clubs.
        </p>
        <div className="mt-8" data-testid="fcn-status-key">
          <p className="mono mb-3 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-500">Status vocabulary</p>
          <StatusKey />
        </div>

        <div className="mt-10 grid gap-px bg-zinc-800 md:grid-cols-2" data-testid="fcn-demonstrates">
          {DEMONSTRATES.map(([title, body, status]) => (
            <div key={title} className="bg-black p-6" data-testid={`fcn-item-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="flex flex-wrap items-center gap-3">
                <h3 className="font-display text-base uppercase tracking-tight text-white">{title}</h3>
                <StatusTag status={status} />
              </div>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 border-l-2 border-ochre bg-[#0b0b0b] p-6 md:p-8" data-testid="fcn-funding">
          <p className="overline text-ochre">Initial funding</p>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-zinc-300 md:text-base">
            {e?.funding_language ||
              "The founder intends to invest in and mobilise the initial funding required to begin delivering the first demonstration projects. Each project remains subject to feasibility, approvals, partnerships, funding and transparent implementation arrangements."}
          </p>
          <p className="mono mt-5 text-[0.55rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-500">
            No project on this platform is described as funded unless the money has been formally committed and can
            be evidenced.
          </p>
        </div>

        <div className="mt-14 flex flex-wrap gap-3">
          <Link to="/plans" className="btn-primary" data-testid="fcn-plans-cta">
            The national plan <ArrowUpRight size={13} />
          </Link>
          <Link to="/ecosystem" className="btn-ghost" data-testid="fcn-ecosystem-cta">
            The football ecosystem
          </Link>
          <Link to="/apply" className="btn-ghost" data-testid="fcn-proposals-cta">
            Submit a project
          </Link>
        </div>
      </div>

      <RecommendedEntities />
    </div>
  );
}
